import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';
import type { DomainEntityType } from '@clawhire/shared';
import { todayIso } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { formatDate, formatRelativeDays } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';
import { SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';

export interface FollowUp {
  id: string;
  text: string;
  dueOn?: string | null;
  done: boolean;
  doneAt?: string | null;
  entityType?: DomainEntityType;
  entityId?: string;
}

export interface FollowUpListProps {
  entityType: DomainEntityType;
  entityId: string;
  /** Pass when the detail endpoint already includes `followUps[]`. */
  items?: FollowUp[];
  canEdit?: boolean;
  onChanged?: () => void;
  /** Hide completed items (default: show them struck through at the end). */
  hideDone?: boolean;
  className?: string;
}

function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

/** Checklist of follow-ups with add / complete / delete (`/follow-ups`). Overdue items are red. */
export function FollowUpList({
  entityType,
  entityId,
  items: itemsProp,
  canEdit = true,
  onChanged,
  hideDone = false,
  className,
}: FollowUpListProps) {
  const queryClient = useQueryClient();
  const key = queryKeys.followUps(entityType, entityId);
  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrapList<FollowUp>(await api.get<unknown>('/follow-ups', { query: { entityType, entityId } })),
    enabled: itemsProp === undefined,
  });
  const today = todayIso();
  const items = React.useMemo(() => {
    const list = [...(itemsProp ?? query.data ?? [])].filter((f) => !hideDone || !f.done);
    list.sort((a, b) => {
      if (a.done !== b.done) return a.done ? 1 : -1;
      return (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999');
    });
    return list;
  }, [itemsProp, query.data, hideDone]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: key });
    onChanged?.();
  };
  const onError = (e: Error) => toast.error(e.message);

  const [text, setText] = React.useState('');
  const [dueOn, setDueOn] = React.useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      api.post('/follow-ups', { entityType, entityId, text: text.trim(), dueOn: dueOn ?? undefined }),
    onSuccess: async () => {
      setText('');
      setDueOn(null);
      await refresh();
    },
    onError,
  });
  const toggle = useMutation({
    mutationFn: (f: FollowUp) =>
      f.done ? api.patch(`/follow-ups/${f.id}`, { done: false }) : api.post(`/follow-ups/${f.id}/complete`),
    onSuccess: refresh,
    onError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/follow-ups/${id}`),
    onSuccess: refresh,
    onError,
  });

  const isLoading = itemsProp === undefined && query.isLoading;

  return (
    <section className={cn('space-y-3', className)} aria-label="Follow-ups">
      {isLoading ? (
        <SkeletonLines lines={3} />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No follow-ups.</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((f) => {
            const overdue = !f.done && f.dueOn && f.dueOn < today;
            const checkboxId = `follow-up-${f.id}`;
            return (
              <li key={f.id} className="group flex items-start gap-2 rounded-md px-1 py-1 hover:bg-muted/50">
                <Checkbox
                  id={checkboxId}
                  checked={f.done}
                  disabled={!canEdit || toggle.isPending}
                  onCheckedChange={() => toggle.mutate(f)}
                  className="mt-0.5"
                />
                <label
                  htmlFor={checkboxId}
                  className={cn(
                    'flex-1 cursor-pointer text-sm',
                    f.done && 'text-muted-foreground line-through',
                  )}
                >
                  {f.text}
                  {f.dueOn ? (
                    <span
                      className={cn(
                        'ml-2 inline-flex items-center gap-1 text-xs',
                        overdue ? 'text-destructive' : 'text-muted-foreground',
                      )}
                    >
                      <CalendarDays className="h-3 w-3" aria-hidden="true" />
                      <time dateTime={f.dueOn}>{formatDate(f.dueOn)}</time>
                      {!f.done ? (
                        <span className="hidden sm:inline">({formatRelativeDays(f.dueOn, today)})</span>
                      ) : null}
                    </span>
                  ) : null}
                </label>
                {canEdit ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="h-6 w-6 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                    aria-label={`Delete follow-up: ${f.text}`}
                    onClick={() => remove.mutate(f.id)}
                    disabled={remove.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {canEdit ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) add.mutate();
          }}
        >
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a follow-up…"
            aria-label="New follow-up"
            className="h-9 min-w-[10rem] flex-1"
            maxLength={1000}
          />
          <DateInput value={dueOn} onChange={setDueOn} aria-label="Due on" className="h-9" />
          <Button type="submit" size="sm" className="h-9" disabled={!text.trim()} loading={add.isPending}>
            <Plus aria-hidden="true" /> Add
          </Button>
        </form>
      ) : null}
    </section>
  );
}
